import type { ValidationDetails } from '@/types/api';

/**
 * Error normalizado de la API.
 *
 * El backend siempre responde `{ success: false, error: { message, details? } }`
 * (ver `backend/src/middleware/error-handler.ts`), y este cliente lo convierte en una
 * instancia de `ApiError` con el status HTTP — así las pantallas pueden mostrar
 * `error.message` tal cual (viene en español, listo para el usuario).
 *
 * `status === 0` significa que la petición nunca llegó al backend (servidor caído,
 * sin red, CORS): se distingue para poder mostrar un mensaje distinto.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly details?: ValidationDetails;
  readonly originalError?: unknown;

  constructor(status: number, message: string, details?: ValidationDetails, originalError?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.originalError = originalError;
  }

  /** 401 — token ausente/expirado: el handler global limpia la sesión y manda a /login. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** 403 — no es miembro del tenant o le falta el rol ADMIN. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** 404 — no existe, o existe pero es de otro tenant (el backend no filtra existencia). */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** 400 — datos inválidos (regla de negocio o validación de Zod en `details`). */
  get isValidationError(): boolean {
    return this.status === 400;
  }

  /** 409 — conflicto (ej. email ya registrado, json de pedido duplicado). */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** No hubo respuesta del backend. */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

/** Mensaje para mostrar cuando la petición no llegó al backend. */
export const NETWORK_ERROR_MESSAGE =
  'No se pudo conectar con el servidor. Verifica que el backend esté corriendo (npm run dev en backend/) y que VITE_API_URL apunte a él.';

/** Extrae un mensaje mostrable desde cualquier error (ApiError, Error nativo o desconocido). */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Ocurrió un error inesperado.';
}
